import { useCallback, useEffect, useState } from "react";
import { participantConnectedEvent } from "./Events/RoomEventsFunction/participantConnectedEvent.js";
import { trackSubscribedEvent } from "./Events/RoomEventsFunction/TrackSubscribed/trackSubscribedEvent.js";
import { getRoom } from "./Room/roomService";
import { trackUnsubscribedEvent } from "./Events/RoomEventsFunction/TrackUnsubscribed/trackUnsubscribedEvent.js";
import { handleLocalUserDisconnectFromTheCall, handleRemotePartcipantDisconnect, updateAudioStatus } from "./Room/roomFunction";
import LocalTrackPublicationFailed from "./Events/RoomEventsFunction/LocalTrackPublication/LocalTrackPublicationFailed.jsx";
import TrackSubscriptionFailed from "./Events/RoomEventsFunction/TrackSubscriptionFailed/TrackSubscriptionFailed.jsx";
import { localNetworkQualityLevelChanged } from "./Events/RoomEventsFunction/NetworkQualityLevelChanged/localNetworkQualityLevelChanged.js";
import { trackEnabledInRoomEvent } from "./Events/RoomEventsFunction/TrackEnabledInRoom/trackEnabledInRoomEvent.js";
import { trackDisabledInRoomEvent } from "./Events/RoomEventsFunction/TrackDisabledInRoom/trackDisabledInRoomEvent.js";
import { trackPublishedInRoomEvent } from "./Events/RoomEventsFunction/TrackPublishedInRoom/trackPublishedInRoomEvent.js";
import { localTrackPublication } from "./Events/RoomEventsFunction/LocalTrackPublication/localTrackPublication.js";
import useParticipants from "../../hooks/useParticipants/useParticipants.jsx";
import { LogFirebase } from "../../services/firestore.js";
import { checkiOSUserAudio } from "./AudioCall/AudioCallUtils.js";
import { setStartLocalAudio } from "../../reducers/twilioEvents-reducer.js";
import { useDispatch } from "react-redux";
import { setIsAudioButtonClickDisable } from "../../reducers/common-reducer.js";
import liveCaptionService from "../../services/liveCaptionService.js";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../../constants/FirebaseConstants.js";
import useCPUPressure from "../../hooks/useCPUPressure/useCPUPressure.jsx";

const TwilioRoomEvent = () => {
    const room = getRoom();
    const totalParticipants = useParticipants();
    const dispatch = useDispatch();
    const [localTrackPublicationFailed, setLocalTrackPublicationFailed] = useState({});
    const [localTrackSubscriptionFailed, setLocalTrackSubscriptionFailed] = useState({});

    // CPU pressure monitoring - logs to Firebase (logs "not supported" on unsupported browsers)
    useCPUPressure();

    const onDisconnected = useCallback(() => {
        handleLocalUserDisconnectFromTheCall();
    }, []);

    const handleParticipantConnected = useCallback(
        (participant) => {
            participantConnectedEvent(participant, totalParticipants);
        },
        [room]
    );

    const handleTrackSubscribed = useCallback(
        (track, remoteTrackPrublication, participant) => {
            trackSubscribedEvent({ track, remoteTrackPrublication, participant });
        },
        [room]
    );

    const handleLocalNQLevelChanged = useCallback(
        (participant) => {
            localNetworkQualityLevelChanged(participant);
        },
        [room]
    );

    const handleTrackUnsubscribed = useCallback(
        (track, remoteTrackPrublication, participant) => {
            trackUnsubscribedEvent(track, remoteTrackPrublication, participant);
        },
        [room]
    );

    const handleLocalTrackPublished = useCallback(
        (track) => {
            localTrackPublication(track);
            if (track.kind === "audio") {
                dispatch(setStartLocalAudio(true));
                dispatch(setIsAudioButtonClickDisable(false));
                updateAudioStatus();
            }
        },
        [room]
    );

    const handleTrackEnabledInRoom = useCallback(
        (track, participant) => {
            trackEnabledInRoomEvent(track, participant);
            if (track.kind === "audio") {
                updateAudioStatus();
            }
        },
        [room]
    );

    const handleTrackDisabledInRoom = useCallback(
        (track, participant) => {
            trackDisabledInRoomEvent(track, participant);
            if (track.kind === "audio") {
                updateAudioStatus();
            }
        },
        [room]
    );

    const handleTrackPublishedInRoom = useCallback(
        (publication, participant) => {
            if (publication.kind === "audio") {
                updateAudioStatus(500);
            }
            trackPublishedInRoomEvent(publication, participant);
        },
        [room]
    );

    const participantDisconnected = useCallback(
        (participant) => {
            handleRemotePartcipantDisconnect(participant);
        },
        [room]
    );

    const handleTrackEnabledForLocalParticipant = useCallback(
        (track) => {
            LogFirebase("TwilioEvents", "Localparticipant_trackEnabled", {
                track_details: track.kind,
            });
            checkiOSUserAudio();
            handleLocalTrackEnabled(track);
        },
        [room]
    );

    const handleTrackDisabledForLocalParticipant = useCallback(
        (track) => {
            LogFirebase("TwilioEvents", "Localparticipant_trackDisabled", {
                track_details: track.kind,
            });
            if (track.kind === "audio") {
                updateAudioStatus();
            }
        },
        [room]
    );

    const handleLocalTrackEnabled = (track) => {
        if (track.kind == "audio") {
            dispatch(setStartLocalAudio(true));
            dispatch(setIsAudioButtonClickDisable(false));
            updateAudioStatus();
        }
    };
    useEffect(() => {
        if (room) {
            const handleLocalTrackPublicationFailed = (error, track) => {
                setLocalTrackPublicationFailed({ error, track });
            };

            const handleLocalTrackSubscriptionFailed = (error, track) => {
                setLocalTrackSubscriptionFailed({ error, track });
            };

            const handleTrackUnpublished = (publication, participant) => {
                LogFirebase("TwilioEvents", "trackUnpublishedInRoom", {
                    track_details: publication?.kind,
                    identity: participant?.identity,
                });
            };

            // Telemetry: Track Warning Event - fires when video track has issues like freezing
            const handleTrackWarning = (name, publication, participant) => {
                if (name === "recording-media-lost") {
                    LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.TrackWarning, {
                        trackName: publication?.track?.name,
                        participant_identity: participant?.identity,
                        message: `LocalTrack ${publication?.track?.name} is not recording media.`,
                    });

                    room.once("trackWarningsCleared", (publication, participant) => {
                        LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.localTrack_warnings_cleared, {
                            trackName: publication?.track?.name,
                            participant_identity: participant?.identity,
                        });
                    });
                }
            };

            // Telemetry: Listen for video dimension changes on remote video tracks
            const handleTrackDimensionsChanged = (track, participant) => {
                LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.VideoDimensionsChanged, {
                    track_name: track?.name,
                    width: track?.dimensions?.width,
                    height: track?.dimensions?.height,
                    trackSid: track?.sid,
                    identity: participant?.identity,
                });
            };

            room.on("participantConnected", handleParticipantConnected);
            room.on("participantDisconnected", participantDisconnected);
            room.on("trackSubscribed", handleTrackSubscribed);
            room.on("trackUnsubscribed", handleTrackUnsubscribed);
            room.on("disconnected", onDisconnected);
            room.on("trackPublished", handleTrackPublishedInRoom);
            room.on("trackUnpublished", handleTrackUnpublished);
            room.on("trackEnabled", handleTrackEnabledInRoom);
            room.on("trackDisabled", handleTrackDisabledInRoom);
            room.on("transcription", (transcriptionEvent) => {
                console.log(`trans__ID ${transcriptionEvent.participant}: ${transcriptionEvent.transcription}`);
                console.log("trans__: ", transcriptionEvent);
                liveCaptionService.addCaption(transcriptionEvent);
            });
            room.localParticipant.on("trackPublished", handleLocalTrackPublished);
            room.localParticipant.on("trackEnabled", handleTrackEnabledForLocalParticipant);
            room.localParticipant.on("trackDisabled", handleTrackDisabledForLocalParticipant);
            room.localParticipant.on("trackPublicationFailed", handleLocalTrackPublicationFailed);
            room.localParticipant.on("trackSubscriptionFailed", handleLocalTrackSubscriptionFailed);
            room.localParticipant.on("networkQualityLevelChanged", () => handleLocalNQLevelChanged(room.localParticipant));

            // Telemetry: Register track warning listener on room
            room.on("trackWarning", handleTrackWarning);

            room.on("trackDimensionsChanged", handleTrackDimensionsChanged);

            return () => {
                room.off("participantConnected", handleParticipantConnected);
                room.off("participantDisconnected", participantDisconnected);
                room.off("disconnected", onDisconnected);
                room.off("trackPublished", handleTrackPublishedInRoom);
                room.off("trackUnpublished", handleTrackUnpublished);
                room.off("trackEnabled", handleTrackEnabledInRoom);
                room.off("trackDisabled", handleTrackDisabledInRoom);

                room.localParticipant.off("trackPublished", handleLocalTrackPublished);
                room.localParticipant.off("trackPublicationFailed", handleLocalTrackPublicationFailed);
                room.localParticipant.off("trackDisabled", handleTrackDisabledForLocalParticipant);
                room.localParticipant.off("trackSubscriptionFailed", handleLocalTrackSubscriptionFailed);
                room.localParticipant.off("networkQualityLevelChanged", handleLocalNQLevelChanged);

                // Telemetry: Cleanup listeners
                room.off("trackWarning", handleTrackWarning);
                room.off("trackDimensionsChanged", handleTrackDimensionsChanged);
            };
        }
    }, [room]);

    return (
        <>
            {localTrackPublicationFailed?.track && (
                <LocalTrackPublicationFailed track={localTrackPublicationFailed.track} error={localTrackPublicationFailed.error} />
            )}
            {localTrackSubscriptionFailed?.track && (
                <TrackSubscriptionFailed track={localTrackSubscriptionFailed.track} error={localTrackSubscriptionFailed.error} />
            )}
        </>
    );
};

export default TwilioRoomEvent;
